// /models — Catalog page.
//
// Compact title/search row, followed by filters and display controls.
// The same header serves the scrollable grid and native virtualized list;
// controls wrap on smaller screens.
//
// URL contract (backward-compat with the old page):
//   ?tag=<slug>     → pre-checks that tag facet on mount
//   ?company=<id>   → scopes the catalog to that company on mount
//                     (shown as an active filter chip in the toolbar)
//
// User actions don't write back to the URL — keeping the URL as a
// one-way deep-link contract avoids history-noise from every facet
// click and keeps the implementation honest.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, Pressable, RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MetaHead from '@/components/MetaHead';
import StatusContainer from '@/components/StatusContainer';
import ModelCard from '@/components/Models/ModelCard';
import ModelListRow, { CATALOG_COLS } from '@/components/Models/ModelListRow';
import CatalogBrowseShell, { CATALOG_VIEW_MODES } from '@/components/Catalog/CatalogBrowseShell';
import CatalogSortDropdown from '@/components/Catalog/CatalogSortDropdown';
import CatalogTopBarChips from '@/components/Catalog/CatalogTopBarChips';
import CatalogScopeDropdown from '@/components/Dropdowns/CatalogScopeDropdown';
import CatalogFacetsDropdown from '@/components/Dropdowns/CatalogFacetsDropdown';
import PageSearchInput from '@/components/Inputs/PageSearchInput';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { useAuthStore, useCompanyStore, useModelStore } from '@/store';
import {
  colorForSlug,
  filterModels,
  isNewModel,
  sortModels,
} from '@/utils/model_catalog';

// How many skeleton cards to render while the initial fetch resolves.
const SKELETON_COUNT = 12;

// Search debounce window for the server-side search call.
const SEARCH_DEBOUNCE_MS = 350;

const CATALOG_RENDER_BATCH = 40;
const CATALOG_BATCH_THRESHOLD = 80;

// How many viewport-heights of runway to keep below the fold before the
// next catalog page is fetched. Loading this early keeps infinite scroll
// from hitting a hard stop (and a visible pop) at the bottom.
const CATALOG_LOAD_AHEAD_VIEWPORTS = 2;

const EmptyState = ({ isXS, query, activePinned, hasFilters, onClear, t, colors }) => {
  let message = t('pages.models.noResults');
  if (!query && !hasFilters) {
    if (activePinned === 'frequent') message = t('pages.models.emptyFrequent');
    else if (activePinned === 'favorites') message = t('pages.models.emptyFavorites');
  }
  const showClear = !!query || hasFilters;
  return (
    <View style={{ alignItems: 'center', paddingVertical: isXS ? 40 : 64, gap: 8 }}>
      <Icon name="ghost" size={24} color={colors.text_color_tertiary} />
      <Text color="tertiary">{message}</Text>
      {showClear && (
        <Pressable
          onPress={onClear}
          style={{
            paddingHorizontal: 12, height: 28, borderRadius: 6,
            alignItems: 'center', justifyContent: 'center',
            backgroundColor: colors.solid_color_tertiary,
            ...(Platform.OS === 'web' && { cursor: 'pointer' }),
          }}
        >
          <Text size="xs" weight="semibold">{t('pages.models.clearFilters')}</Text>
        </Pressable>
      )}
    </View>
  );
};

const ModelsPage = React.memo(() => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const router = useRouter();
  const { tag: urlTag, company: urlCompany } = useLocalSearchParams();
  const { isXS, isMD, isXL } = useResponsiveness();

  // ─────────────── store wiring ──────────────────────────────────────
  const models                = useModelStore(state => state.models);
  const searchResults         = useModelStore(state => state.searchResults);
  const isLoadingModelTags    = useModelStore(state => state.isLoadingModelTags);
  const isLoadingModelTagsWithModels = useModelStore(state => state.isLoadingModelTagsWithModels);
  const modelTagsWithModelsError     = useModelStore(state => state.modelTagsWithModelsError);
  const isSearching           = useModelStore(state => state.isSearching);
  const searchError           = useModelStore(state => state.searchError);
  const frequentModelIds      = useModelStore(state => state.frequentModelIds) || [];

  const getAllModelTags         = useModelStore(state => state.getAllModelTags);
  const modelTagsMap            = useModelStore(state => state.modelTags);
  const fetchModelTags          = useModelStore(state => state.fetchModelTags);
  const fetchFrequentModels     = useModelStore(state => state.fetchFrequentModels);
  const fetchAllModels          = useModelStore(state => state.fetchAllModels);
  const isLoadingAllModels      = useModelStore(state => state.isLoadingAllModels);
  const isFetchedAllModels      = useModelStore(state => state.isFetchedAllModels);
  const allModelsError          = useModelStore(state => state.allModelsError);
  const allModelsPagination     = useModelStore(state => state.pagination?.allModels);
  const searchModels            = useModelStore(state => state.searchModels);
  const clearSearch             = useModelStore(state => state.clearSearch);

  const isLoadingCompanies      = useCompanyStore(state => state.isLoadingCompanies);
  const companiesError          = useCompanyStore(state => state.companiesError);
  const fetchModelCompanies     = useCompanyStore(state => state.fetchModelCompanies);
  const fetchModelsByCompany    = useCompanyStore(state => state.fetchModelsByCompany);
  const getAllCompanies         = useCompanyStore(state => state.getAllCompanies);
  const getCompanyById          = useCompanyStore(state => state.getCompanyById);

  const favoriteModelIds        = useAuthStore(state => state.user?.favoriteModelIds) || [];
  const userId                  = useAuthStore(state => state.user?.id);
  const hasUser                 = Boolean(userId);
  const requireAuth             = useRequireAuth();

  // Stable resolver for the sort comparator that doesn't recreate every
  // render (otherwise the `useMemo` for sorted results would invalidate
  // on every keystroke).
  const getCompanyName = useCallback((id) => getCompanyById(id)?.name || '', [getCompanyById]);

  // ─────────────── local UI state ────────────────────────────────────
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef(null);

  const [activePinned, setActivePinned] = useState('all');
  // Category is a single-select picker — at most one tag at a time.
  // Stored as a Set so the filter pipeline (which treats facets as a
  // generic AND-of-tags list) stays unchanged; the toggle handler
  // enforces the at-most-one invariant.
  const [selectedFacets, setSelectedFacets] = useState(() => {
    const initial = new Set();
    if (urlTag) initial.add(`tag:${urlTag}`);
    return initial;
  });
  const [activeCompany, setActiveCompany] = useState(urlCompany || null);
  const [sort, setSort] = useState('newest');
  const [view, setView] = useState('grid');
  const [refreshing, setRefreshing] = useState(false);
  const [visibleCatalogCount, setVisibleCatalogCount] = useState(CATALOG_BATCH_THRESHOLD);
  const filteredLengthRef = useRef(0);

  const useNativeMobileList = Platform.OS !== 'web' && isXS && view === 'list';

  // Re-sync URL params when they change (e.g. user clicks a /models?tag=foo
  // link from elsewhere while the page is already mounted). Since category
  // is single-select, a URL tag replaces the active selection.
  useEffect(() => {
    if (urlTag) setSelectedFacets(new Set([`tag:${urlTag}`]));
  }, [urlTag]);

  useEffect(() => {
    setActiveCompany(urlCompany || null);
  }, [urlCompany]);

  // ─────────────── data bootstrap ────────────────────────────────────
  // Load tags + companies + the first page of models. We use
  // `fetchAllModels` (paginates through `/json/models`) instead of the
  // company-grouped endpoint — that one only returns the top 9 models
  // per company across the first 20 companies, so larger catalogs were
  // silently truncated by ~20 entries.
  //
  // We pass `autoLoadAll: false` so the store doesn't immediately chain
  // through every page (`?page=2,3,4,…`) on mount — instead the scroll
  // handler below pulls the next page when the user actually nears the
  // bottom of the catalog. Filters/sort/facets still run in-memory
  // against whatever has been loaded so far, so switching them never
  // re-hits the API.
  useEffect(() => {
    if (getAllModelTags().length === 0 && !isLoadingModelTags) fetchModelTags();
    fetchModelCompanies();
    fetchAllModels(1, { autoLoadAll: false });
    // Frequent ids are derived from per-user server-side usage history;
    // skip for anonymous visitors so the call doesn't 401 and the
    // "Frequent" scope shows our sign-in CTA instead of an empty grid.
    if (hasUser) fetchFrequentModels?.();
  }, [
    getAllModelTags,
    isLoadingModelTags,
    fetchModelTags,
    fetchModelCompanies,
    fetchAllModels,
    fetchFrequentModels,
    hasUser,
  ]);

  useEffect(() => {
    if (!urlCompany) return;
    // `fetchModelsByCompany` hits `/json/companies/:id/models` directly
    // and doesn't depend on the companies list being loaded, so we
    // don't need to await `fetchModelCompanies` here (avoids a duplicate
    // call to that endpoint).
    fetchModelsByCompany(urlCompany);
  }, [urlCompany, fetchModelsByCompany]);

  // ─────────────── search ────────────────────────────────────────────
  // Local instant filter while the server search debounces. The server
  // result eventually fills `searchResults` and replaces `filtered`.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setDebouncedQuery('');
      clearSearch();
      return;
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(query.trim());
      searchModels(query.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, clearSearch, searchModels]);

  // Cleanup search results on unmount so re-entering the page doesn't
  // briefly flash stale matches.
  useEffect(() => {
    return () => {
      clearSearch();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [clearSearch]);

  // ─────────────── derived data ──────────────────────────────────────
  const allModels = useMemo(() => Object.values(models), [models]);

  // Apply scope (company → pinned). Search bypasses scope so users can
  // discover any model from anywhere by typing.
  const scopedModels = useMemo(() => {
    if (debouncedQuery) return searchResults;

    let list = allModels;

    if (activeCompany) {
      list = list.filter(m => m.companyId === activeCompany);
    }

    if (activePinned === 'frequent') {
      const order = new Map(frequentModelIds.map((id, i) => [id, i]));
      return list
        .filter(m => order.has(m.id))
        .sort((a, b) => order.get(a.id) - order.get(b.id));
    }
    if (activePinned === 'favorites') {
      const set = new Set(favoriteModelIds);
      return list.filter(m => set.has(m.id));
    }
    return list;
  }, [debouncedQuery, searchResults, allModels, activeCompany, activePinned, frequentModelIds, favoriteModelIds]);

  // Pinned counts always reference the company-scoped (but
  // pre-search) list so the user sees how many favorites they have
  // even when scoped to "Frequent".
  const pinnedCounts = useMemo(() => {
    const base = activeCompany ? allModels.filter(m => m.companyId === activeCompany) : allModels;
    // Global "All" count: server `pagination.total` from `/json/models` (stable while
    // infinite-scroll pages load). Company scope has no that total — use loaded rows.
    const allCount = !activeCompany && (allModelsPagination?.total > 0)
      ? allModelsPagination.total
      : base.length;
    return {
      all: allCount,
      frequent: base.filter(m => frequentModelIds.includes(m.id)).length,
      favorites: base.filter(m => favoriteModelIds.includes(m.id)).length,
    };
  }, [allModels, activeCompany, frequentModelIds, favoriteModelIds, allModelsPagination]);

  // Tag facets for the left rail. On the global "All" catalog (no company,
  // no server search), counts come from `ModelTag.stats.modelCount` loaded
  // with `/json/model-tags/all` (denormalized in seed) so they stay correct
  // while models infinite-scroll in. In narrower scopes (company / frequent
  // / favorites / search), counts are derived from the scoped in-memory
  // list so they still read as "how many in this view".
  const facets = useMemo(() => {
    const useServerTagCounts =
      !debouncedQuery && !activeCompany && activePinned === 'all';

    if (useServerTagCounts) {
      return Object.values(modelTagsMap)
        .filter(
          (tg) =>
            tg?.slug
            && tg.flags?.isEnabled !== false
            && (tg.stats?.modelCount ?? 0) > 0,
        )
        .map((tg) => ({
          id: `tag:${tg.slug}`,
          slug: tg.slug,
          label: tg.name || tg.slug,
          color: colorForSlug(tg.slug),
          count: tg.stats?.modelCount ?? 0,
        }))
        .sort((a, b) => b.count - a.count);
    }

    const tagMap = new Map();
    scopedModels.forEach(m => {
      (m.tags || []).forEach(tg => {
        if (!tg?.slug) return;
        const id = `tag:${tg.slug}`;
        if (!tagMap.has(id)) {
          tagMap.set(id, {
            id,
            slug: tg.slug,
            label: tg.name || tg.slug,
            color: colorForSlug(tg.slug),
            count: 0,
          });
        }
        tagMap.get(id).count++;
      });
    });
    return Array.from(tagMap.values()).sort((a, b) => b.count - a.count);
  }, [
    modelTagsMap,
    scopedModels,
    debouncedQuery,
    activeCompany,
    activePinned,
  ]);

  // Apply local search (instant feedback) + facet filters + sort.
  const filtered = useMemo(() => {
    // When debounced server search is active, scopedModels already
    // came from the server; only apply facets + sort.
    let list = debouncedQuery ? scopedModels : filterModels(scopedModels, query);

    if (selectedFacets.size > 0) {
      const required = Array.from(selectedFacets).map(id => id.replace(/^tag:/, ''));
      list = list.filter(m => {
        const slugs = (m.tags || []).map(t => t.slug);
        return required.every(req => slugs.includes(req));
      });
    }

    // Frequent has its own intentional ordering (most-recent first).
    // Honor it when the user hasn't picked an explicit sort.
    if (activePinned === 'frequent' && sort === 'trending' && !debouncedQuery) {
      return list;
    }

    return sortModels(list, sort, { getCompanyName });
  }, [scopedModels, query, debouncedQuery, selectedFacets, sort, activePinned, getCompanyName]);

  filteredLengthRef.current = filtered.length;

  useEffect(() => {
    setVisibleCatalogCount(CATALOG_BATCH_THRESHOLD);
  }, [debouncedQuery, selectedFacets, activeCompany, activePinned, sort, view]);

  const visibleFiltered = useMemo(() => {
    if (Platform.OS === 'web' || !isXS || filtered.length <= CATALOG_BATCH_THRESHOLD) {
      return filtered;
    }
    return filtered.slice(0, visibleCatalogCount);
  }, [filtered, visibleCatalogCount, isXS]);

  // Toolbar "Showing %COUNT% of %TOTAL%": for the global All scope, `%TOTAL%`
  // is the server's `pagination.total` from `/json/models` (e.g. 119) rather
  // than however many rows have been infinite-scrolled into memory so far.
  const catalogResultsTotal = useMemo(() => {
    if (debouncedQuery) return scopedModels.length;
    if (activePinned === 'all' && !activeCompany && (allModelsPagination?.total > 0)) {
      return allModelsPagination.total;
    }
    return scopedModels.length;
  }, [debouncedQuery, activePinned, activeCompany, allModelsPagination, scopedModels.length]);

  // ─────────────── handlers ──────────────────────────────────────────
  // Category is single-select: tapping the active tag clears it, tapping
  // a different tag replaces the selection. The prop on the dropdown is
  // still called `onToggle` because that's the component's contract.
  const toggleFacet = useCallback((id) => {
    setSelectedFacets(prev => {
      if (prev.has(id)) return new Set();
      return new Set([id]);
    });
  }, []);

  const clearFacets = useCallback(() => setSelectedFacets(new Set()), []);

  // Switching the pinned scope wipes facets — carrying them across
  // scope changes feels haunted ("why am I still filtered to image
  // models in Favorites?"). Cheaper to start fresh.
  const onPinnedChange = useCallback((id) => {
    // Frequent/Favorites scopes are owner-only — gate anonymous visitors
    // here so they don't bounce off empty states with no signposting.
    if ((id === 'frequent' || id === 'favorites') && !hasUser) {
      const isFavorites = id === 'favorites';
      requireAuth(
        () => { setActivePinned(id); clearFacets(); },
        {
          source: isFavorites ? 'models_favorites_scope' : 'models_frequent_scope',
          title: isFavorites
            ? 'Sign in to see your favorite models'
            : 'Sign in to see your frequent models',
          subtitle: isFavorites
            ? 'Favorites are tied to your account so they follow you across devices.'
            : 'We use your usage history to surface the models you reach for most.',
          icon: isFavorites ? 'star' : 'clock-rotate-left',
        }
      );
      return;
    }
    setActivePinned(id);
    clearFacets();
  }, [clearFacets, hasUser, requireAuth]);

  const clearAllFilters = useCallback(() => {
    setQuery('');
    clearFacets();
    setActivePinned('all');
    setActiveCompany(null);
    if (urlTag || urlCompany) router.push('/models');
  }, [clearFacets, urlTag, urlCompany, router]);

  const onModelPress = useCallback((modelId) => {
    router.push(`/m/${modelId}`);
  }, [router]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // Reset the "already fetched" flag and re-pull from page 1 so
      // pull-to-refresh actually re-hydrates the catalog instead of
      // no-oping on the cache. Also clear `isLoadingAllModels` in case
      // a page-N fetch was in flight when refresh fired — otherwise the
      // store-level guard would short-circuit our page-1 reload. Stay
      // on a single page — additional pages come back in via the scroll
      // handler.
      useModelStore.setState({
        isFetchedAllModels: false,
        isLoadingAllModels: false,
        allModelIds: [],
      });
      await fetchAllModels(1, { autoLoadAll: false });
      if (urlCompany) await fetchModelsByCompany(urlCompany);
    } finally {
      setRefreshing(false);
    }
  }, [urlCompany, fetchAllModels, fetchModelsByCompany]);

  // ─────────────── pinned items + sort options ───────────────────────
  const pinnedItems = useMemo(() => [
    { id: 'all',       label: t('pages.models.scopeAll'),       icon: 'grid-2',            color: '#3B82F6' },
    { id: 'frequent',  label: t('pages.models.scopeFrequent'),  icon: 'clock-rotate-left', color: '#F59E0B' },
    { id: 'favorites', label: t('pages.models.scopeFavorites'), icon: 'star',              color: '#EC4899' },
  ], [t]);

  const sortOptions = useMemo(() => [
    { id: 'trending', label: t('pages.models.sortTrending'), icon: 'fire',           iosIcon: 'flame' },
    { id: 'newest',   label: t('pages.models.sortNewest'),   icon: 'sparkles',       iosIcon: 'sparkles' },
    { id: 'name',     label: t('pages.models.sortNameAZ'),   icon: 'arrow-down-a-z', iosIcon: 'textformat.abc' },
    { id: 'cost',     label: t('pages.models.sortCheapest'), icon: 'coin',           iosIcon: 'creditcard' },
  ], [t]);

  const modelsScopeOptions = useMemo(
    () => pinnedItems.map((item) => ({
      id: item.id,
      label: item.label,
      icon: item.icon,
      color: item.color,
    })),
    [pinnedItems],
  );

  // ─────────────── loading / error ───────────────────────────────────
  const isInitialLoading =
    allModels.length === 0 &&
    (isLoadingAllModels || isLoadingCompanies || isLoadingModelTagsWithModels || isLoadingModelTags);

  const initialError =
    allModels.length === 0 &&
    (modelTagsWithModelsError || companiesError || allModelsError);

  // ─────────────── grid columns ──────────────────────────────────────
  // Auto-fit columns by viewport. The 220px rail eats a lot of usable
  // width on tablet/MD, so 3-up cards get squashed there — drop to 2-up
  // until LG. XS = 1 (rail collapses to pills), SM/MD = 2, LG = 3, XL = 4.
  const colCount = isXS ? 1 : isMD ? 2 : isXL ? 4 : 3;
  const cardRows = useMemo(() => {
    const source = Platform.OS !== 'web' && isXS ? visibleFiltered : filtered;
    const rows = [];
    for (let i = 0; i < source.length; i += colCount) {
      rows.push(source.slice(i, i + colCount));
    }
    return rows;
  }, [visibleFiltered, filtered, colCount, isXS]);

  // Memoized active company, used in the toolbar chip (direct map lookup).
  const activeCompanyRecord = useCompanyStore(
    useCallback(
      (state) => (activeCompany ? state.companies[activeCompany] ?? null : null),
      [activeCompany],
    ),
  );

  // ─────────────── render ────────────────────────────────────────────
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const modelsToolbarChips = useMemo(() => {
    const out = [];
    if (activeCompanyRecord) {
      out.push({
        key: 'company',
        label: activeCompanyRecord.name,
        leadingIcon: 'building',
        onDismiss: () => {
          setActiveCompany(null);
          if (urlCompany) router.push('/models');
        },
      });
    }
    for (const id of selectedFacets) {
      const cat = facets.find((c) => c.id === id);
      if (!cat) continue;
      out.push({
        key: id,
        label: cat.label,
        onDismiss: () => toggleFacet(id),
      });
    }
    return out;
  }, [activeCompanyRecord, urlCompany, router, selectedFacets, facets, toggleFacet]);

  const modelsViewToggle = useMemo(() => (
    <View style={styles.viewToggle}>
      {[
        { id: CATALOG_VIEW_MODES.LIST, icon: 'bars', label: t('pages.models.listView') },
        { id: CATALOG_VIEW_MODES.GRID, icon: 'grid-2', label: t('pages.models.gridView') },
      ].map(option => (
        <Pressable
          key={option.id}
          onPress={() => setView(option.id)}
          accessibilityRole="button"
          accessibilityLabel={option.label}
          accessibilityState={{ selected: view === option.id }}
          style={({ hovered, pressed }) => [
            styles.viewToggleButton,
            (view === option.id || hovered || pressed) && styles.viewToggleButtonActive,
          ]}
        >
          <Icon name={option.icon} size={13}
            color={view === option.id ? colors.text_color_primary : colors.text_color_tertiary} />
        </Pressable>
      ))}
    </View>
  ), [styles, t, view, colors.text_color_primary, colors.text_color_tertiary]);

  const modelsHeader = useMemo(() => (
    <View style={styles.catalogHeader}>
      <View style={styles.headingRow}>
        <View style={styles.titleRow}>
          <View style={styles.titleCopy}>
            <Text tag="h2" size={isXS ? 'xxl' : 'xxxl'} weight="bold" style={styles.heading}>
              {t('navigation.models')}
            </Text>
            <Text size="s" color="secondary" style={styles.subtitle}>
              {t('pages.models.description')}
            </Text>
          </View>
          {isXS && modelsViewToggle}
        </View>
        <PageSearchInput
          value={query}
          onChangeText={setQuery}
          placeholder={t('pages.models.searchPlaceholder')}
          catalogVariant
          noPadding
          containerStyle={styles.headerSearch}
        />
      </View>
      <View style={styles.controlsRow}>
        <View style={styles.filterControls}>
          <CatalogScopeDropdown
            variant="neutral"
            options={modelsScopeOptions}
            value={activePinned}
            onChange={onPinnedChange}
            defaultLabel={t('pages.models.scopeAll')}
          />
          <CatalogFacetsDropdown
            variant="neutral"
            facets={facets}
            selectedIds={selectedFacets}
            onToggle={toggleFacet}
            onClearAll={selectedFacets.size > 0 ? clearFacets : undefined}
            defaultTriggerLabel={t('pages.models.category')}
          />
        </View>
        <View style={styles.displayControls}>
          {!isXS && (
            <Text size="xs" color="tertiary" style={styles.resultsCount}>
              {t('pages.models.showingResults')
                .replace('%COUNT%', String(filtered.length))
                .replace('%TOTAL%', String(catalogResultsTotal))}
            </Text>
          )}
          <CatalogSortDropdown
            value={sort}
            options={sortOptions}
            onChange={setSort}
            label={t('pages.models.sortLabel')}
            showLabel={false}
            size={isXS ? 'medium' : 'small'}
          />
          {!isXS && modelsViewToggle}
        </View>
      </View>
      {modelsToolbarChips.length > 0 && (
        <View style={styles.activeFilters}>
          <CatalogTopBarChips chips={modelsToolbarChips} />
        </View>
      )}
    </View>
  ), [
    styles, isXS, t, query, modelsScopeOptions, activePinned, onPinnedChange,
    facets, selectedFacets, toggleFacet, clearFacets, filtered.length,
    catalogResultsTotal, sort, sortOptions, modelsViewToggle, modelsToolbarChips,
  ]);

  // ─────────────── infinite scroll ───────────────────────────────────
  // Drive `/json/models?page=N` from a scroll-near-bottom signal so we
  // don't stampede the API with every page on mount. The trigger keeps
  // ~CATALOG_LOAD_AHEAD_VIEWPORTS screens of runway below the fold, so
  // the next page is usually rendered before the user scrolls to it
  // instead of popping in after they hit the bottom. Search has its own
  // pagination flow and shouldn't trigger more "all models" pages.
  // In-flight dedupe comes from the store: `isLoadingAllModels` flips
  // synchronously when a fetch starts, so overlapping triggers collapse
  // into a single serial request chain.
  const allModelsHasMore = !!allModelsPagination?.hasMore && !isFetchedAllModels;
  const catalogViewportRef = useRef(0);
  const catalogContentHeightRef = useRef(0);
  const catalogScrollOffsetRef = useRef(0);
  const loadMoreSentinelRef = useRef(null);

  const loadNextCatalogPage = useCallback(() => {
    if (debouncedQuery) return;
    if (!allModelsHasMore) return;
    if (isLoadingAllModels) return;
    const nextPage = (allModelsPagination?.current || 1) + 1;
    fetchAllModels(nextPage, { autoLoadAll: false });
  }, [debouncedQuery, allModelsHasMore, isLoadingAllModels, allModelsPagination, fetchAllModels]);

  const handlePageScroll = useCallback((e) => {
    const { contentOffset, layoutMeasurement, contentSize } = e.nativeEvent || {};
    if (!layoutMeasurement?.height || !contentSize?.height) return;
    catalogViewportRef.current = layoutMeasurement.height;
    catalogContentHeightRef.current = contentSize.height;
    catalogScrollOffsetRef.current = contentOffset.y;
    if (debouncedQuery) return;
    const runway = Math.max(layoutMeasurement.height * CATALOG_LOAD_AHEAD_VIEWPORTS, 900);
    const distanceFromBottom = contentSize.height - (contentOffset.y + layoutMeasurement.height);
    if (distanceFromBottom > runway) return;

    if (Platform.OS !== 'web' && isXS) {
      setVisibleCatalogCount((prev) => Math.min(prev + CATALOG_RENDER_BATCH, filteredLengthRef.current));
    }

    loadNextCatalogPage();
  }, [debouncedQuery, loadNextCatalogPage, isXS]);

  // Top up the runway without waiting for a scroll event: initial load
  // on tall screens, window resizes, and page appends all re-check the
  // remaining runway and chain the next page until there's enough
  // content below the fold.
  const topUpCatalogRunway = useCallback(() => {
    const viewport = catalogViewportRef.current;
    const content = catalogContentHeightRef.current;
    if (!viewport || !content) return;
    const distanceFromBottom = content - (catalogScrollOffsetRef.current + viewport);
    if (distanceFromBottom > viewport * CATALOG_LOAD_AHEAD_VIEWPORTS) return;
    loadNextCatalogPage();
  }, [loadNextCatalogPage]);

  const handleCatalogLayout = useCallback((e) => {
    catalogViewportRef.current = e.nativeEvent?.layout?.height || 0;
    topUpCatalogRunway();
  }, [topUpCatalogRunway]);

  const handleCatalogContentSizeChange = useCallback((_, h) => {
    catalogContentHeightRef.current = h || 0;
    topUpCatalogRunway();
  }, [topUpCatalogRunway]);

  // Re-check after each page settles. Layout/content-size handlers can
  // fire while page N is still in-flight (when loadNextCatalogPage no-ops),
  // and on web `onContentSizeChange` is not always reliable — so whenever
  // a page lands we re-measure the runway (or prefetch one page ahead if
  // the ScrollView hasn't reported sizes yet).
  useEffect(() => {
    if (debouncedQuery) return;
    if (!allModelsHasMore || isLoadingAllModels) return;
    if (allModels.length === 0) return;
    if (catalogViewportRef.current && catalogContentHeightRef.current) {
      topUpCatalogRunway();
      return;
    }
    // No layout metrics yet (common on first paint / RN web). Warm only
    // the immediate next page once so we don't chain the whole catalog
    // before the ScrollView reports its size.
    const pagesLoaded = allModelsPagination?.current || 1;
    if (pagesLoaded <= 1) loadNextCatalogPage();
  }, [
    debouncedQuery,
    allModelsHasMore,
    isLoadingAllModels,
    allModels.length,
    allModelsPagination,
    topUpCatalogRunway,
    loadNextCatalogPage,
  ]);

  // Web: observe the load-more footer with a large rootMargin so the next
  // page starts fetching ~CATALOG_LOAD_AHEAD_VIEWPORTS screens before the
  // sentinel reaches the fold. More reliable than scroll-math alone on
  // RN web, where ScrollView size/scroll events can be flaky.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof IntersectionObserver === 'undefined') return;
    if (debouncedQuery || !allModelsHasMore) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;

    const rootMargin = `${Math.round(CATALOG_LOAD_AHEAD_VIEWPORTS * 100)}% 0px`;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadNextCatalogPage();
        }
      },
      { root: null, rootMargin, threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [debouncedQuery, allModelsHasMore, loadNextCatalogPage, allModels.length]);

  const handleNativeListEndReached = useCallback(() => {
    if (debouncedQuery) return;
    if (!allModelsHasMore && visibleCatalogCount >= filteredLengthRef.current) return;
    if (isLoadingAllModels) return;

    setVisibleCatalogCount((prev) => Math.min(prev + CATALOG_RENDER_BATCH, filteredLengthRef.current));

    loadNextCatalogPage();
  }, [debouncedQuery, allModelsHasMore, isLoadingAllModels, loadNextCatalogPage, visibleCatalogCount]);

  const renderMobileModelRow = useCallback(({ item: m }) => (
    <ModelListRow
      model={m}
      company={getCompanyById(m.companyId)}
      onPress={onModelPress}
      isXS
    />
  ), [getCompanyById, onModelPress]);

  const modelKeyExtractor = useCallback((m) => m.id, []);

  const refreshControl = useMemo(() => (
    <RefreshControl
      refreshing={refreshing}
      onRefresh={onRefresh}
      tintColor={colors.text_color_secondary}
      colors={[colors.text_color_secondary]}
    />
  ), [refreshing, onRefresh, colors.text_color_secondary]);

  const showLoadingMoreFooter =
    !debouncedQuery
    && allModelsHasMore
    && allModels.length > 0
    && !initialError;

  const hasActiveFilter =
    !debouncedQuery
    && (selectedFacets.size > 0 || activePinned !== 'all' || !!activeCompany);
  useEffect(() => {
    if (!hasActiveFilter) return;
    if (!allModelsHasMore) return;
    if (isLoadingAllModels) return;
    const nextPage = (allModelsPagination?.current || 1) + 1;
    fetchAllModels(nextPage, { autoLoadAll: false });
  }, [
    hasActiveFilter,
    allModelsHasMore,
    isLoadingAllModels,
    allModelsPagination,
    fetchAllModels,
  ]);

  const mobileListHeader = useMemo(() => (
    <>
      {modelsHeader}
      <CatalogBrowseShell
        isXS={isXS}
        splitGap={0}
        bodyGap={0}
        noBodyPadding
        hideRail
        suppressMobilePills
      >
        <View style={styles.contentColumn}>
          {isInitialLoading ? (
            <View style={styles.gridScroll}>
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => {
                const colIdx = i % colCount;
                if (colIdx === 0) {
                  return (
                    <View key={`skel-row-${i}`} style={styles.gridRow}>
                      {Array.from({ length: colCount }).map((__, j) => (
                        <View key={`skel-${i}-${j}`} style={{ flex: 1, minWidth: 0 }}>
                          <ModelCard isLoading />
                        </View>
                      ))}
                    </View>
                  );
                }
                return null;
              })}
            </View>
          ) : isSearching && filtered.length === 0 ? (
            <View style={styles.statusWrap}>
              <Text color="secondary">{t('pages.common.searchResults')}…</Text>
            </View>
          ) : searchError && filtered.length === 0 ? (
            <View style={styles.errorWrap}>
              <StatusContainer
                type="error"
                message={searchError}
                actionText={t('pages.common.retry')}
                onAction={() => searchModels(debouncedQuery)}
              />
            </View>
          ) : null}
        </View>
      </CatalogBrowseShell>
    </>
  ), [
    modelsHeader,
    isXS,
    styles,
    isInitialLoading,
    colCount,
    isSearching,
    filtered.length,
    searchError,
    t,
    debouncedQuery,
    searchModels,
  ]);

  const mobileListFooter = useMemo(() => (
    <>
      {showLoadingMoreFooter && (
        <View style={styles.loadMoreFooter}>
          <Text size="xs" color="tertiary">
            {isLoadingAllModels
              ? t('pages.models.loadingMore') || 'Loading more models…'
              : t('pages.models.scrollToLoadMore') || 'Scroll for more'}
          </Text>
        </View>
      )}
      <View style={styles.footerWrap}>
        <AppDashboardFooter />
      </View>
    </>
  ), [showLoadingMoreFooter, styles, isLoadingAllModels, t]);

  if (useNativeMobileList && !initialError) {
    return (
      <View style={styles.root}>
        <MetaHead
          title={t('pages.models.title')}
          description={t('pages.models.subtitle')}
          canonical="/models"
        />
        <FlashList
          data={isInitialLoading ? [] : filtered}
          renderItem={renderMobileModelRow}
          keyExtractor={modelKeyExtractor}
          estimatedItemSize={76}
          extraData={`${query}:${sort}:${activePinned}:${selectedFacets.size}`}
          ListHeaderComponent={mobileListHeader}
          ListFooterComponent={mobileListFooter}
          ListEmptyComponent={
            !isInitialLoading && filtered.length === 0
              ? () => (
                <EmptyState
                  isXS={isXS}
                  query={query}
                  activePinned={activePinned}
                  hasFilters={selectedFacets.size > 0 || !!activeCompany}
                  onClear={clearAllFilters}
                  t={t}
                  colors={colors}
                />
              )
              : null
          }
          onEndReached={handleNativeListEndReached}
          onEndReachedThreshold={0.35}
          refreshControl={refreshControl}
          contentContainerStyle={styles.listScrollMobile}
          showsVerticalScrollIndicator={false}
        />
      </View>
    );
  }

  return (
    <View style={styles.root}>
      <MetaHead
        title={t('pages.models.title')}
        description={t('pages.models.subtitle')}
        canonical="/models"
      />

      <ScrollView
        style={styles.pageScroll}
        contentContainerStyle={styles.pageScrollContent}
        showsVerticalScrollIndicator={false}
        onScroll={handlePageScroll}
        onLayout={handleCatalogLayout}
        onContentSizeChange={handleCatalogContentSizeChange}
        scrollEventThrottle={16}
        refreshControl={(
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={colors.text_color_secondary}
            colors={[colors.text_color_secondary]}
          />
        )}
      >
        {modelsHeader}

        <CatalogBrowseShell
          isXS={isXS}
          splitGap={0}
          bodyGap={0}
          noBodyPadding={view === 'list'}
          hideRail
          suppressMobilePills
        >
        <View style={styles.contentColumn}>
          {initialError ? (
            <View style={styles.errorWrap}>
              <StatusContainer
                type="error"
                message={initialError}
                actionText={t('pages.common.retry')}
                onAction={onRefresh}
              />
            </View>
          ) : isInitialLoading ? (
            <View style={styles.gridScroll}>
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => {
                const colIdx = i % colCount;
                if (colIdx === 0) {
                  return (
                    <View key={`skel-row-${i}`} style={styles.gridRow}>
                      {Array.from({ length: colCount }).map((__, j) => (
                        <View key={`skel-${i}-${j}`} style={{ flex: 1, minWidth: 0 }}>
                          <ModelCard isLoading />
                        </View>
                      ))}
                    </View>
                  );
                }
                return null;
              })}
            </View>
          ) : isSearching && filtered.length === 0 ? (
            <View style={styles.statusWrap}>
              <Text color="secondary">{t('pages.common.searchResults')}…</Text>
            </View>
          ) : searchError && filtered.length === 0 ? (
            <View style={styles.errorWrap}>
              <StatusContainer
                type="error"
                message={searchError}
                actionText={t('pages.common.retry')}
                onAction={() => searchModels(debouncedQuery)}
              />
            </View>
          ) : view === 'grid' ? (
            <View style={styles.gridScroll}>
              {cardRows.map((row, i) => (
                <View key={`row-${i}`} style={styles.gridRow}>
                  {row.map(m => (
                    <View key={m.id} style={{ flex: 1, minWidth: 0 }}>
                      <ModelCard
                        model={m}
                        company={getCompanyById(m.companyId)}
                        onPress={onModelPress}
                      />
                    </View>
                  ))}
                  {/* Pad partial last row so cards don't stretch */}
                  {row.length < colCount && Array.from({ length: colCount - row.length }).map((_, j) => (
                    <View key={`pad-${i}-${j}`} style={{ flex: 1 }} />
                  ))}
                </View>
              ))}
              {filtered.length === 0 && (
                <EmptyState
                  isXS={isXS}
                  query={query}
                  activePinned={activePinned}
                  hasFilters={selectedFacets.size > 0 || !!activeCompany}
                  onClear={clearAllFilters}
                  t={t}
                  colors={colors}
                />
              )}
            </View>
          ) : (
            // ── List view ──
            isXS ? (
              <View style={styles.listScrollMobile}>
                {visibleFiltered.map(m => (
                  <ModelListRow
                    key={m.id}
                    model={m}
                    company={getCompanyById(m.companyId)}
                    onPress={onModelPress}
                    isXS
                  />
                ))}
                {filtered.length === 0 && (
                  <EmptyState
                    isXS={isXS}
                    query={query}
                    activePinned={activePinned}
                    hasFilters={selectedFacets.size > 0 || !!activeCompany}
                    onClear={clearAllFilters}
                    t={t}
                    colors={colors}
                  />
                )}
              </View>
            ) : (
              <View>
                <View style={styles.listHeader}>
                  {CATALOG_COLS.map(c => (
                    <View
                      key={c.id}
                      style={[
                        listColumnStyle(c),
                        { justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start' },
                      ]}
                    >
                      <Text size="xxs" weight="bold" color="tertiary" style={styles.listHeaderLabel}>
                        {translateColumnLabel(c.id, t)}
                      </Text>
                    </View>
                  ))}
                </View>
                <View style={{ paddingBottom: 24 }}>
                  {filtered.map(m => (
                    <ModelListRow
                      key={m.id}
                      model={m}
                      company={getCompanyById(m.companyId)}
                      onPress={onModelPress}
                    />
                  ))}
                  {filtered.length === 0 && (
                    <EmptyState
                      isXS={isXS}
                      query={query}
                      activePinned={activePinned}
                      hasFilters={selectedFacets.size > 0 || !!activeCompany}
                      onClear={clearAllFilters}
                      t={t}
                      colors={colors}
                    />
                  )}
                </View>
              </View>
            )
          )}
          {showLoadingMoreFooter && (
            <View ref={loadMoreSentinelRef} style={styles.loadMoreFooter} collapsable={false}>
              <Text size="xs" color="tertiary">
                {isLoadingAllModels
                  ? t('pages.models.loadingMore') || 'Loading more models…'
                  : t('pages.models.scrollToLoadMore') || 'Scroll for more'}
              </Text>
            </View>
          )}
        </View>
        </CatalogBrowseShell>

        <View style={styles.footerWrap}>
          <AppDashboardFooter />
        </View>
      </ScrollView>
    </View>
  );
});

// Shared column-sizing rule so header and rows stay perfectly aligned
// without horizontal scroll. A column with `flex` stretches to fill the
// remaining space; a column with `width` stays fixed (used for Company
// and Cost where the content is short and fixed-format).
export const listColumnStyle = (c) => {
  const base = { flexDirection: 'row', alignItems: 'center' };
  if (c.flex) return { ...base, flex: c.flex, minWidth: c.minWidth || 0, paddingRight: 12 };
  return { ...base, width: c.width };
};

// Map a list-view column id to its translated header label.
const translateColumnLabel = (id, t) => {
  switch (id) {
    case 'name':    return t('pages.models.colModel');
    case 'company': return t('pages.models.colCompany');
    case 'inputs':  return t('pages.models.colInputs');
    case 'outputs': return t('pages.models.colOutputs');
    case 'cost':    return t('pages.models.colCost');
    default:        return id;
  }
};

const createStyles = (colors, isXS) => StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  pageScroll: {
    flex: 1,
  },
  pageScrollContent: {
    flexGrow: 1,
  },
  catalogHeader: {
    paddingHorizontal: isXS ? 15 : 20,
    paddingTop: isXS ? 16 : 24,
    paddingBottom: 16,
    gap: isXS ? 16 : 20,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border_color_secondary,
  },
  headingRow: {
    flexDirection: isXS ? 'column' : 'row',
    alignItems: isXS ? 'stretch' : 'center',
    justifyContent: 'space-between',
    gap: 16,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: isXS ? undefined : 1,
    gap: 12,
  },
  titleCopy: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  heading: {
    letterSpacing: 0,
  },
  subtitle: {
    lineHeight: 22,
  },
  headerSearch: {
    width: isXS ? '100%' : 320,
    paddingBottom: 0,
  },
  controlsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  filterControls: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
  },
  displayControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 12,
    marginLeft: 'auto',
  },
  resultsCount: {
    fontVariant: ['tabular-nums'],
    marginRight: 4,
  },
  viewToggle: {
    flexDirection: 'row',
    padding: 3,
    borderRadius: 8,
    backgroundColor: colors.solid_color_block,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  viewToggleButton: {
    width: isXS ? 38 : 30,
    height: isXS ? 34 : 24,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 5,
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
  },
  viewToggleButtonActive: {
    backgroundColor: colors.solid_color_tertiary,
  },
  activeFilters: {
    flexDirection: 'row',
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
  },
  gridScroll: {
    paddingTop: isXS ? 12 : 20,
    gap: 14,
  },
  gridRow: {
    flexDirection: 'row',
    gap: 14,
  },
  listScrollMobile: {
    padding: 12,
    gap: 8,
  },
  listHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 32,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_primary,
    ...(Platform.OS === 'web' && { position: 'sticky', top: 0, zIndex: 1 }),
  },
  listHeaderLabel: {
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  statusWrap: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  errorWrap: {
    padding: 24,
    justifyContent: 'center',
  },
  loadMoreFooter: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 24,
  },
  footerWrap: {
    paddingHorizontal: isXS ? 16 : 28,
  },
});

// `ModelsPage` is already wrapped in `React.memo`, so the route export just
// renders it directly — an extra memo layer here would only add a no-op
// wrapper component to every navigation between this and another tab.
export default function ModelsRoute() {
  return <ModelsPage />;
}
